import { useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { DriverFormDialog } from '@/components/drivers/DriverFormDialog';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { rpc } from '@/api/client';

export interface CanonicalDriver {
  id: string;
  firstName: string;
  lastName: string;
  nationality: string;
  aliasDriverIds: string[];
  sessionDriverIds: string[];
}

interface PaginatedResponse {
  drivers: CanonicalDriver[];
  totalCount: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 10;

export function DriversTab() {
  const { t } = useI18n();
  const { showToast } = useToast();

  const [items, setItems] = useState<CanonicalDriver[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<CanonicalDriver | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const fetchPage = useCallback(async (p: number) => {
    try {
      const data = await rpc<PaginatedResponse>('ListCanonicalDrivers', { page: p, pageSize: PAGE_SIZE });
      setItems(data.drivers ?? []);
      setTotalCount(data.totalCount ?? 0);
      setPage(data.page ?? p);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => fetchPage(page), [fetchPage, page]);

  useState(() => { fetchPage(1); });

  const goToPage = (p: number) => {
    const clamped = Math.max(1, Math.min(totalPages, p));
    setPage(clamped);
    fetchPage(clamped);
  };

  const handleSave = async (values: { canonicalDriverId: string; aliasDriverIds: string[]; sessionDriverIds: string[] }) => {
    if (!values.canonicalDriverId) {
      showToast(t('crud.requiredField', { field: t('drivers.canonicalDriver') }), 'error');
      return;
    }

    setSaving(true);
    try {
      if (editItem) {
        await rpc('UpdateCanonicalDriver', {
          canonicalDriverId: editItem.id,
          aliasDriverIds: values.aliasDriverIds,
          sessionDriverIds: values.sessionDriverIds,
        });
        showToast(t('crud.updated', { entity: t('entity.driver') }));
      } else {
        await rpc('CreateCanonicalDriver', {
          canonicalDriverId: values.canonicalDriverId,
          aliasDriverIds: values.aliasDriverIds,
          sessionDriverIds: values.sessionDriverIds,
        });
        showToast(t('crud.created', { entity: t('entity.driver') }));
      }
      setShowForm(false);
      setEditItem(null);
      await refresh();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: CanonicalDriver) => {
    const name = `${item.firstName} ${item.lastName}`.trim();
    if (!confirm(t('crud.confirmDelete', { entity: t('entity.driver').toLowerCase(), name }))) return;
    try {
      await rpc('DeleteCanonicalDriver', { id: item.id });
      showToast(t('crud.deleted', { entity: t('entity.driver') }));
      await refresh();
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  };

  const startEdit = (item: CanonicalDriver) => {
    setEditItem(item);
    setShowForm(true);
  };

  const startAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditItem(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold tracking-tight">{t('tab.drivers')}</h2>
        <button
          onClick={startAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
            bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer"
        >
          <Plus size={14} />
          {t('common.add')}
        </button>
      </div>

      {/* Form Dialog */}
      {showForm && (
        <DriverFormDialog
          open
          editing={editItem}
          saving={saving}
          onSave={handleSave}
          onClose={cancelForm}
        />
      )}

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} />
      ) : totalCount === 0 ? (
        <EmptyState
          icon="🏎️"
          message={t('crud.emptyState', { title: t('tab.drivers').toLowerCase(), entity: t('entity.driver').toLowerCase() })}
        />
      ) : (
        <div className="card overflow-hidden">
          {/* Desktop table */}
          <table className="w-full border-collapse hidden md:table">
            <thead>
              <tr className="divider">
                <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                  {t('drivers.canonicalDriver')}
                </th>
                <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                  {t('drivers.aliasesHeader')}
                </th>
                <th className="w-[80px] text-right px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted" />
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const totalAliases = (item.aliasDriverIds?.length || 0) + (item.sessionDriverIds?.length || 0);
                return (
                  <tr key={item.id} className={`hover:bg-bg-hover transition-colors ${i < items.length - 1 ? 'divider' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="text-sm text-text-primary">
                        {item.firstName} {item.lastName}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {totalAliases > 0 ? (
                        <div className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-bg-raised border border-border-subtle text-[0.65rem] text-text-secondary">
                          {totalAliases}
                        </div>
                      ) : (
                        <span className="text-[0.65rem] text-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => startEdit(item)}
                          className="p-1.5 rounded text-text-muted hover:text-text-primary transition-colors cursor-pointer"
                          title={t('common.edit')}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-1.5 rounded text-text-muted hover:text-red transition-colors cursor-pointer"
                          title={t('common.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Mobile list */}
          <div className="md:hidden">
            {items.map((item, i) => {
              const totalAliases = (item.aliasDriverIds?.length || 0) + (item.sessionDriverIds?.length || 0);
              return (
                <div key={item.id} className={`p-4 hover:bg-bg-hover transition-colors ${i < items.length - 1 ? 'divider' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-1">
                      <div className="text-sm text-text-primary">
                        {item.firstName} {item.lastName}
                      </div>
                      <div className="text-[0.65rem] text-text-muted">
                        {t('drivers.aliases')}: {totalAliases}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => startEdit(item)}
                        className="p-2 rounded text-text-muted hover:text-text-primary transition-colors cursor-pointer"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-2 rounded text-text-muted hover:text-red transition-colors cursor-pointer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-border-subtle text-xs text-text-muted">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="p-1 rounded hover:bg-bg-hover disabled:opacity-30 transition-colors cursor-pointer"
              >
                <ChevronLeft size={14} />
              </button>
              <span>{page} / {totalPages}</span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className="p-1 rounded hover:bg-bg-hover disabled:opacity-30 transition-colors cursor-pointer"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
